import { z } from 'zod';

type PageLimitValue = number | string | null | undefined;

const pageLimitSchema = z
  .number({
    invalid_type_error: 'Page limit must be a number',
  })
  .int('Page limit must be an integer')
  .min(1, 'Minimum page limit is 1')
  .max(10000, 'Maximum page limit is 10000');

const isIntegerString = (value: string) => /^\d+$/u.test(value.trim());

const toNumberIfValidInteger = (value: PageLimitValue): number | undefined => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) return undefined;
    if (!isIntegerString(trimmed)) return undefined;
    return Number(trimmed);
  }

  return undefined;
};

export const validatePageLimit = (value: PageLimitValue) => {
  if (value === undefined || value === null || value === '') {
    return 'Page limit is required';
  }

  const normalized = toNumberIfValidInteger(value);
  if (normalized === undefined) {
    return 'Page limit must be an integer';
  }

  const parsed = pageLimitSchema.safeParse(normalized);
  if (parsed.success) {
    return undefined;
  }

  return parsed.error.issues[0]?.message || 'Invalid page limit';
};

export const normalizePageLimitValue = (
  value: PageLimitValue
): number | undefined => {
  const normalized = toNumberIfValidInteger(value);
  if (normalized === undefined) {
    return undefined;
  }

  const parsed = pageLimitSchema.safeParse(normalized);
  if (parsed.success) {
    return parsed.data;
  }

  return undefined;
};
